import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  isValidEmail,
  isValidPhone,
  isValidTelegram,
} from "../../config/regexValidator";
import { formatDate, formatTime } from "../../helpers/order_format";
import { AppDispatch } from "../../store";
import {
  postOrder,
  selectError,
  selectLoading,
} from "../../store/slices/postOrderSlice";
import { getProductCategories } from "../../store/slices/productCategoriesSlice";
import { getProducts } from "../../store/slices/productsSlice";
import { Products } from "../../types/types";

import {
  BackButton,
  ButtonContainer,
  Checkbox,
  CheckboxFieldWrapper,
  CheckboxWrapper,
  ErrorMessage,
  FieldWrapper,
  Input,
  Label,
  NextButton,
  PolicyText,
  StepContainer,
} from "./styles";
import {  UserData } from "./useBooking";

import {readySignedConsentsData} from "../../config/readySignedConsentsData";


type BookingStepUserProps = {
  userData: UserData;
  onChange: (data: UserData) => void;
  onSubmit: () => void;
  onBack: () => void;
  selectedDate: Date | null;
  selectedSlot: string | null;
  product: Products | null;
};

export const BookingStepUser: React.FC<BookingStepUserProps> = ({
  userData,
  onChange,
  onSubmit,
  onBack,
  selectedDate,
  selectedSlot,
  product,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [localData, setLocalData] = useState(userData);
  const [agree, setAgree] = useState(false);
  const [consent, setConsent] = useState(false);
  const [notificationConsent, setNotificationConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [isInitialServiceSet, setIsInitialServiceSet] = useState(false);

  const isLoading = useSelector(selectLoading);
  const error = useSelector(selectError);

  useEffect(() => {
    dispatch(getProductCategories());
    dispatch(getProducts());
  }, [dispatch]);

  useEffect(() => {
    onChange(localData);
  }, [localData, onChange]);

  useEffect(() => {
    if (product && !localData.service && !isInitialServiceSet) {
      setLocalData((prev) => ({
        ...prev,
        service: String(product.id),
      }));
      setIsInitialServiceSet(true);
    }
  }, [product, localData.service, isInitialServiceSet]);

  const handleChange = (field: keyof UserData, value: string) => {
    setLocalData((prev) => ({ ...prev, [field]: value }));

    if (touchedFields[field]) {
      validateField(field, value);
    }
  };

  const handleBlur = (field: keyof UserData) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
    validateField(field, localData[field]);
  };

  const validateField = (field: keyof UserData, value: string) => {
    let error = "";

    if (field === "phone" && (!value || !isValidPhone(value))) {
      error = "Введите корректный номер";
    }
    if (field === "telegram" && (!value || !isValidTelegram(value))) {
      error = "Неверный формат. Пример: @nickname";
    }
    if (field === "email" && value && !isValidEmail(value)) {
      error = "Введите корректный email";
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleSubmit = () => {
    const requiredFields: (keyof UserData)[] = ["phone", "telegram", "email"];
    const newTouched = Object.fromEntries(requiredFields.map((f) => [f, true]));

    setTouchedFields((prev) => ({ ...prev, ...newTouched, agree: true, consent: true, notificationConsent: true }));

    const newErrors: Record<string, string> = {};

    if (!localData.username) newErrors.username = "Введите имя";
    if (!localData.service) newErrors.service = "Выберите услугу";
    if (!isValidPhone(localData.phone)) newErrors.phone = "Введите корректный номер";
    if (!isValidTelegram(localData.telegram)) newErrors.telegram = "Неверный формат. Пример: @nickname";
    if (localData.email && !isValidEmail(localData.email)) newErrors.email = "Введите корректный email";
    if (!agree) newErrors.agree = "Необходимо согласие";
    if (!consent) newErrors.consent = "Необходимо согласие на обработку данных";
    if (!notificationConsent) newErrors.notificationConsent = "Необходимо согласие на получение уведомлений";

    setErrors(newErrors);

    const hasErrors = Object.keys(newErrors).length > 0;

    if (hasErrors || !selectedDate || !selectedSlot) return;

    const orderData = {
      username: localData.username || "",
      consultation_date: formatDate(selectedDate),
      consultation_time: formatTime(selectedSlot),
      product: { id: Number(localData.service) },
      is_booked: false,
      is_completed: false,
      question: "",
      telephone: localData.phone,
      telegram: localData.telegram || "",
      email: localData.email || "",
      linkedin_url: "",
      consents: [
          readySignedConsentsData.consent(),
          readySignedConsentsData.policy(),
          readySignedConsentsData.notification_consent(),
      ],
    };

    dispatch(postOrder(orderData))
      .unwrap()
      .then(() => onSubmit())
      .catch((err) => console.error("Ошибка отправки заказа:", err));
  };

  const isFormValid =
      localData.service &&
      isValidPhone(localData.phone) &&
      isValidTelegram(localData.telegram) &&
      agree &&
      consent &&
      notificationConsent;

  return (
    <StepContainer>
      <FieldWrapper>
        <Label>Имя</Label>
        <Input
          type="text"
          value={localData.username}
          onChange={(e) => handleChange("username", e.target.value)}
          onBlur={() => handleBlur("username")}
          placeholder="Имя"
          hasError={!!errors.username}
        />
      </FieldWrapper>

      <FieldWrapper>
        <Label>Номер телефона*</Label>
        <Input
          type="tel"
          value={localData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          onBlur={() => handleBlur("phone")}
          placeholder="+7 (999) 999-99-99"
          hasError={!!errors.phone}
        />
        {touchedFields.phone && errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        <Label>Телеграм*</Label>
        <Input
          type="text"
          value={localData.telegram}
          onChange={(e) => handleChange("telegram", e.target.value)}
          onBlur={() => handleBlur("telegram")}
          placeholder="@nickname"
          hasError={!!errors.telegram}
        />
        {touchedFields.telegram && errors.telegram && <ErrorMessage>{errors.telegram}</ErrorMessage>}
      </FieldWrapper>

      <FieldWrapper>
        <Label>E-mail</Label>
        <Input
          type="email"
          value={localData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          onBlur={() => handleBlur("email")}
          placeholder="Введите ваш e-mail"
          hasError={!!errors.email}
        />
        {touchedFields.email && errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
      </FieldWrapper>

      <CheckboxFieldWrapper>
        <CheckboxWrapper>
          <Checkbox
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
          />
          <PolicyText>
              Нажимая «Отправить», вы соглашаетесь с{" "}
            <a href="/policy" target="_blank">
                Политикой конфиденциальности
            </a>.
          </PolicyText>
        </CheckboxWrapper>
        {touchedFields.agree && errors.agree && <ErrorMessage>{errors.agree}</ErrorMessage>}

        <CheckboxWrapper>
          <Checkbox
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          <PolicyText>
              Нажимая «Отправить», вы соглашаетесь с{" "}
            <a href="/consent" target="_blank">
                Cогласием на обработку персональных данных
            </a>.
          </PolicyText>
        </CheckboxWrapper>
        {touchedFields.consent && errors.consent && <ErrorMessage>{errors.consent}</ErrorMessage>}

        <CheckboxWrapper>
          <Checkbox
            type="checkbox"
            checked={notificationConsent}
            onChange={(e) => setNotificationConsent(e.target.checked)}
          />
          <PolicyText>
              Нажимая «Отправить», вы соглашаетесь с{" "}
            <a href="/notification-consent" target="_blank">
                Cогласием на получение рассылки
            </a>.
          </PolicyText>
        </CheckboxWrapper>
        {touchedFields.notificationConsent && errors.notificationConsent && <ErrorMessage>{errors.notificationConsent}</ErrorMessage>}
      </CheckboxFieldWrapper>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonContainer>
        <BackButton onClick={onBack}>Назад</BackButton>
        <NextButton onClick={handleSubmit} disabled={!isFormValid || isLoading}>
          {isLoading ? "Отправка..." : "Отправить"}
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};
